import { useEffect, useRef, useState } from 'react';
import './ContactForm.css';

/*
  This form posts directly to Brevo's hosted form endpoint instead of our own
  server — Brevo validates it, runs it through Cloudflare Turnstile (using
  Brevo's own site key, so we don't need a Turnstile account of our own), and
  adds/updates the contact on their side. Our server never sees the
  submission, so there's no /api/contact call here.

  The field names/ids (FIRSTNAME, LASTNAME, EMAIL, QUESTION, IS_VEGAN,
  IS_VEGAN_CURIOUS, VEGAN_YEARS), the hidden honeypot/locale fields, and the
  DOM structure (#sib-form-container, #error-message, #success-message,
  .form__entry, .entry__error) all have to match what the Brevo form was
  built with in the dashboard and what sibforms' main.js script expects —
  this was reverse-engineered from the raw HTML of the form's own hosted
  page (Contacts > Forms in Brevo) rather than copied from a design spec.
*/
const BREVO_FORM_ACTION =
  'https://eb1d8f89.sibforms.com/serve/MUIFADoMJX4x37ZyxYW80-Uu1o8oFDR1mUnVkLOvVepkMhM7AMimvvLFre2KqP2WHlCsjCwOe__i1RimK7EzVpugnz3JPk5h1DkptuyQna0Fmrnt5RQA_gXxhEskLnXi4vkFdPJaG6oh6KZy7lBPQr3LPdw6lNJMsCDNwCtzFQwCJ2vZLG8ZkyblIazf0TPnCGrhJxtlgkqMAEpI2Q==';
const TURNSTILE_SITEKEY = '0x4AAAAAAEtaHTcy0uyFDfUO';

const FieldError = ({ id, message }) => (
  <label id={id} className="entry__error entry__error--primary" role="alert">
    {message}
  </label>
);

// Deliberately simple — just catches typos like a missing @ or domain; the
// server-side (Brevo) validation is still the real gatekeeper.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function ContactForm({ onSuccess }) {
  const [isVegan, setIsVegan] = useState(false);
  const [isVeganCurious, setIsVeganCurious] = useState(false);
  const [errors, setErrors] = useState({});
  const containerRef = useRef(null);
  const yearsEnabled = isVegan || isVeganCurious;

  // Runs in the capture phase on an ancestor of the form so it fires before
  // main.js's own submit listener on the form; when something is invalid we
  // stop the event there so main.js never sees (or posts) the submission.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const validate = (event) => {
      const form = event.target;
      const nextErrors = {};
      if (!form.elements.FIRSTNAME.value.trim()) {
        nextErrors.FIRSTNAME = 'Please enter your first name.';
      }
      if (!EMAIL_PATTERN.test(form.elements.EMAIL.value.trim())) {
        nextErrors.EMAIL = 'Please enter a valid email address.';
      }
      setErrors(nextErrors);

      const firstInvalid = ['FIRSTNAME', 'EMAIL'].find((name) => nextErrors[name]);
      if (!firstInvalid) return;

      event.preventDefault();
      event.stopPropagation();
      const field = form.elements[firstInvalid];
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus({ preventScroll: true });
    };

    container.addEventListener('submit', validate, true);
    return () => container.removeEventListener('submit', validate, true);
  }, []);

  useEffect(() => {
    window.REQUIRED_CODE_ERROR_MESSAGE = 'Please choose a country code';
    window.LOCALE = 'en';
    window.INVALID_NAME = 'Please enter your name.';
    window.EMAIL_INVALID_MESSAGE = window.SMS_INVALID_MESSAGE =
      'Please enter a valid email address.';
    window.REQUIRED_ERROR_MESSAGE = 'This field cannot be left blank.';
    window.GENERIC_INVALID_MESSAGE =
      'The information provided is invalid. Please review the field and try again.';
    window.INVALID_NUMBER = 'Please enter a valid number.';
    // On success, main.js hides #sib-container (the actual form fields)
    // itself when this is true, leaving just the success message panel.
    window.AUTOHIDE = true;
    // Brevo's captcha callback bridges the Turnstile response into the
    // recaptcha-shaped hook main.js already knows how to read.
    window.handleCaptchaResponse = function handleCaptchaResponse() {
      document.getElementById('sib-captcha')?.dispatchEvent(new Event('captchaChange'));
      window.grecaptcha = window.turnstile;
    };

    /*
      Turnstile is loaded once and left in place — it's a vendor singleton,
      and removing/recreating it on every mount makes it execute twice in
      quick succession under StrictMode's double-invoke, which throws inside
      the (cross-origin, so detail-less) script. Its widget is (re)rendered
      per mount by the effect below instead.
    */
    const turnstileSrc = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    if (!document.querySelector(`script[src="${turnstileSrc}"]`)) {
      const script = document.createElement('script');
      script.src = turnstileSrc;
      script.async = true;
      script.defer = true;
      // Cloudflare serves this with Access-Control-Allow-Origin: *, so this
      // gets us a real error message/stack instead of an opaque "Script
      // error." if something throws inside it.
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    }
  }, []);

  /*
    Brevo's main.js, unlike Turnstile, can't be loaded once: when it runs it
    does a one-time document.querySelector('#sib-form') and attaches its submit
    handler (and all its field validation) to that exact element. React
    discards that <form> when the user navigates away from /contact, so on
    coming back the new form has no handler at all — submitting falls through
    to a native POST to Brevo and lands the user on a blank response page.
    So it's re-executed on every mount, after the form is in the DOM.

    The setTimeout keeps StrictMode's mount/unmount/mount double-invoke from
    running it twice (two handlers on one form would submit twice): the first
    pass is cancelled before the script is ever created.
  */
  useEffect(() => {
    const src = 'https://sibforms.com/forms/end-form/build/main.js';
    const timer = setTimeout(() => {
      document.querySelectorAll(`script[src="${src}"]`).forEach((old) => old.remove());
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      // No crossOrigin here: Brevo's script sends no CORS header, so the
      // attribute would just block it from loading.
      document.body.appendChild(script);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  /*
    Turnstile only auto-renders .cf-turnstile elements that exist when its
    script first loads, so on a remount (script already loaded, widget div
    brand new) nothing renders and the captcha silently disappears. If the
    script is already there, render explicitly; if it's still loading, its
    own on-load pass will pick the element up.
  */
  useEffect(() => {
    const el = document.getElementById('sib-captcha');
    if (!el) return undefined;

    let widgetId;
    if (window.turnstile && !el.hasChildNodes()) {
      widgetId = window.turnstile.render(el, {
        sitekey: TURNSTILE_SITEKEY,
        language: 'en',
        callback: window.handleCaptchaResponse,
      });
    }
    return () => {
      if (widgetId !== undefined) window.turnstile?.remove(widgetId);
    };
  }, []);

  // main.js flips this class directly via the DOM (it's not our React state),
  // so a MutationObserver is how the parent page finds out a submission
  // succeeded — used to reflow the rest of the page now that the form itself
  // is gone (see AUTOHIDE above).
  useEffect(() => {
    const successPanel = document.getElementById('success-message');
    if (!successPanel) return undefined;

    const observer = new MutationObserver(() => {
      if (!successPanel.classList.contains('sib-form-message-panel--active')) return;
      if (onSuccess) onSuccess();
      // Wait a frame so the form hiding / page reflow has settled before
      // scrolling, otherwise we'd scroll to where the panel used to be.
      requestAnimationFrame(() => {
        successPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        successPanel.focus({ preventScroll: true });
      });
    });
    observer.observe(successPanel, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [onSuccess]);

  return (
    <div className="ContactForm sib-form">
      <div id="sib-form-container" className="sib-form-container">
        <div id="error-message" className="sib-form-message-panel ContactForm-panel ContactForm-panel--error">
          <div className="sib-form-message-panel__text sib-form-message-panel__text--center">
            <span className="sib-form-message-panel__inner-text">
              Your information could not be saved. Please try again.
            </span>
          </div>
        </div>

        <div
          id="success-message"
          className="sib-form-message-panel ContactForm-panel ContactForm-panel--success"
          role="status"
          tabIndex={-1}
        >
          <div className="sib-form-message-panel__text sib-form-message-panel__text--center">
            <span className="sib-form-message-panel__inner-text">
              Thank you for contacting us! We will get back to you soon!
            </span>
          </div>
        </div>

        <div id="sib-container" ref={containerRef} className="sib-container--large sib-container--vertical">
          <form id="sib-form" method="POST" action={BREVO_FORM_ACTION} data-type="subscription" noValidate>
            <div className="ContactForm-row">
              <div className="form__entry entry_block ContactForm-field">
                <label className="entry__label" htmlFor="FIRSTNAME">First Name *</label>
                <div className="entry__field">
                  <input
                    className="input"
                    type="text"
                    id="FIRSTNAME"
                    name="FIRSTNAME"
                    maxLength={200}
                    autoComplete="given-name"
                    data-required="true"
                    required
                    aria-invalid={Boolean(errors.FIRSTNAME)}
                    aria-describedby="FIRSTNAME-error"
                    onChange={() => errors.FIRSTNAME && setErrors((e) => ({ ...e, FIRSTNAME: undefined }))}
                  />
                </div>
                <FieldError id="FIRSTNAME-error" message={errors.FIRSTNAME} />
              </div>

              <div className="form__entry entry_block ContactForm-field">
                <label className="entry__label" htmlFor="LASTNAME">Last Name</label>
                <div className="entry__field">
                  <input
                    className="input"
                    type="text"
                    id="LASTNAME"
                    name="LASTNAME"
                    maxLength={200}
                    autoComplete="family-name"
                    data-re
                  />
                </div>
                <FieldError />
              </div>
            </div>

            <div className="form__entry entry_block ContactForm-field">
              <label className="entry__label" htmlFor="EMAIL">Email *</label>
              <div className="entry__field">
                <input
                  className="input"
                  type="email"
                  id="EMAIL"
                  name="EMAIL"
                  autoComplete="email"
                  data-required="true"
                  required
                  aria-invalid={Boolean(errors.EMAIL)}
                  aria-describedby="EMAIL-error"
                  onChange={() => errors.EMAIL && setErrors((e) => ({ ...e, EMAIL: undefined }))}
                />
              </div>
              <FieldError id="EMAIL-error" message={errors.EMAIL} />
            </div>

            <div className="form__entry entry_block ContactForm-field">
              <label className="entry__label" htmlFor="QUESTION">Your Question</label>
              <div className="entry__field">
                <textarea
                  className="input"
                  id="QUESTION"
                  name="QUESTION"
                  rows={4}
                  data-required="false"
                />
              </div>
              <FieldError />
            </div>

            <div className="ContactForm-row ContactForm-row--vegan">
              <div className="form__entry entry_mcq ContactForm-checkbox">
                <label>
                  <input
                    type="checkbox"
                    className="input_replaced"
                    id="IS_VEGAN"
                    name="IS_VEGAN"
                    value="1"
                    checked={isVegan}
                    onChange={() => setIsVegan((v) => !v)}
                  />
                  <span>I'm Vegan</span>
                </label>
                <FieldError />
              </div>

              <div className="form__entry entry_mcq ContactForm-checkbox">
                <label>
                  <input
                    type="checkbox"
                    className="input_replaced"
                    id="IS_VEGAN_CURIOUS"
                    name="IS_VEGAN_CURIOUS"
                    value="1"
                    checked={isVeganCurious}
                    onChange={() => setIsVeganCurious((v) => !v)}
                  />
                  <span>I'm Vegan-curious</span>
                </label>
                <FieldError />
              </div>

              <div className="form__entry entry_block ContactForm-field ContactForm-field--years">
                <label className="entry__label" htmlFor="VEGAN_YEARS">Years</label>
                <div className="entry__field">
                  <select
                    className="input"
                    id="VEGAN_YEARS"
                    name="VEGAN_YEARS"
                    defaultValue=""
                    disabled={!yearsEnabled}
                  >
                    <option value="">Select</option>
                    <option value="0">0 - 6 Months</option>
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    <option value="5">5 Years</option>
                    <option value="10">10 Years</option>
                    <option value="20">20+ Years</option>
                  </select>
                </div>
                <FieldError />
              </div>
            </div>

            <div className="form__entry entry_block ContactForm-captcha">
              <div
                className="cf-turnstile g-recaptcha"
                data-sitekey={TURNSTILE_SITEKEY}
                id="sib-captcha"
                data-callback="handleCaptchaResponse"
                data-language="en"
              />
              <FieldError />
            </div>

            <button type="submit" className="ContactForm-submit">
              {/*
                main.js reaches into the submit button for an <svg> to toggle
                a loading-spinner class on at submit time (see
                sib-form-block__button-with-loader in Brevo's own generated
                markup) — without one, `button.querySelector("svg")` is null
                and its removeClass() call throws.
              */}
              <svg className="icon sib-hide-loader-icon" viewBox="0 0 512 512" aria-hidden="true">
                <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" />
              </svg>
              Send Message
            </button>

            <input
              type="text"
              name="email_address_check"
              defaultValue=""
              className="input--hidden"
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />
            <input type="hidden" name="locale" value="en" />
          </form>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;
